import { useEffect, useMemo, useState } from 'react';

import { convertToRaw } from 'draft-js';
import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { IconEdit } from '@components/icons/edit';
import { IconLock } from '@components/icons/lock';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { InfoTooltip } from '@components/info-tooltip';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextEditor, useEditorState } from '@components/text-editor';
import { useIsMobile } from '@hooks/use-is-mobile';
import { makeEditorState } from '@utils/draftjs';

import { AdminAccentBar, AdminNoteInfo, AdminOnlyLabel } from 'components/admin-note-cues';
import { Box } from 'components/Box';
import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { useAuthenticatedUser } from 'features/authentication';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { IJob, IJobNote } from 'models/types';

interface Props {
  job: IJob;
}

export const Notes = observer((props: Props) => {
  const { job } = props;
  const [editing, setEditing] = useState<IJobNote>({});
  const { t } = useStore();

  useEffect(() => {
    job.setUserRead('notes');
  }, [job]);

  return (
    <Stack space={6}>
      <Box flex h="40" alignItems="center">
        <Text font="bold18">{t('job.info.notes.header')}</Text>
      </Box>
      <Separator />

      <NotesTextBox job={job} disabled={editing && editing.id} />
      {job.notes.map((note: IJobNote) => (
        <JobNote key={note.id} note={note} editing={editing} setEditing={setEditing} job={job} />
      ))}
    </Stack>
  );
});

const JobNote = observer(
  ({
    note,
    setEditing,
    editing,
    job,
  }: {
    note: IJobNote;
    setEditing: (note: IJobNote) => void;
    editing: IJobNote;
    job: IJob;
  }) => {
    const { t } = useStore();
    const { showModal } = useModal();
    const me = useAuthenticatedUser();
    const isMobile = useIsMobile();
    function removeModal(note: IJobNote) {
      showModal('COMMON_REMOVE_CONFIRM', {
        _container: {
          minWidth: '200px',
          autoWidth: true,
        },
        data: { id: note.id, confirmText: t('job.info.notes.remove.confirm') },
        confirm: note.remove,
      });
    }

    function editNote(note: IJobNote) {
      setEditing(note);
    }

    function cancelEdit() {
      setEditing({});
    }

    const canEditNote = editing.id !== note.id && note.user?.id === me.id && !isMobile;
    const canDeleteNote =
      editing.id !== note.id && (note.user?.id === me.id || me.role === 'superadmin') && !isMobile;
    return (
      <Stack space={2} orientation="horizontal">
        <Avatar src={note.user.picture} name={note.user.name} size="lg" />
        <Stack space={1} style={{ width: editing && editing.id === note.id ? '100%' : 'auto' }}>
          <Stack space={3} orientation="horizontal" align="flex-start">
            {note.isAdminNote && <AdminAccentBar />}
            {editing && editing.id === note.id ? (
              <NoteEdit key={note.text} note={editing} job={job} onCancel={cancelEdit} />
            ) : (
              <Note key={note.text} note={note} />
            )}
            {(canEditNote || canDeleteNote) && (
              <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
                {canEditNote && (
                  <Menu.Item
                    icon={<IconEdit />}
                    onSelect={() => editNote(note)}
                    label={t('job.info.notes.actions.edit')}
                  />
                )}
                {canDeleteNote && (
                  <Menu.Item
                    icon={<IconTrash />}
                    variant="danger"
                    onSelect={() => removeModal(note)}
                    label={t('job.info.notes.actions.delete')}
                  />
                )}
              </Menu>
            )}
          </Stack>
          <Stack space={2} orientation="horizontal" align="flex-start">
            {note.isAdminNote && (
              <IconLock
                size={16}
                color="elementPurple50"
                tooltip={t('job.info.notes.textbox.adminnote.label')}
              />
            )}
            <Stack space={1}>
              <Text font="regular14" color="textSecondary">
                {note.dateDisplay}
              </Text>
              {note.isAdminNote && <AdminOnlyLabel text={t('job.info.notes.adminonly')} />}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  },
);

const Note = observer(({ note }: { note: IJobNote }) => {
  const { userStore } = useStore();
  return (
    <TextEditor
      key={note.text}
      variant="orange"
      readOnly
      initialEditorState={makeEditorState(note.text)}
    >
      <TextEditorToolbar />
      <TextEditor.Mention getUser={(id) => userStore.items.get(id)} />
      <TextEditor.Input />
    </TextEditor>
  );
});

interface NotesTextBoxProps {
  job: IJob;
  disabled: boolean;
}

const NotesTextBox = observer((props: NotesTextBoxProps) => {
  const { job, disabled } = props;
  const { userStore, t } = useStore();
  const [adding, setAdding] = useState(false);
  const [adminNote, setAdminNote] = useState(false);
  const { editorState, resetEditorState, setEditorState, isEmpty } = useEditorState('');
  const me = useAuthenticatedUser();

  const followers = useMemo(() => {
    let followers = [...job.activeFollowers];
    if (adminNote) {
      followers = followers.filter((f) => f.role === 'admin');
    }
    return followers;
  }, [job.activeFollowers, adminNote]);

  const saveNote = async () => {
    setAdding(true);
    const raw = convertToRaw(editorState.getCurrentContent());
    const success = await job.addNote(JSON.stringify(raw), adminNote);
    if (success) {
      resetEditorState();
      setAdminNote(false);
    }
    setAdding(false);
  };

  return (
    <Box grow>
      <TextEditor
        variant="orange"
        disabled={disabled}
        onChange={setEditorState}
        editorState={editorState}
      >
        <TextEditorToolbar />
        <TextEditor.Input
          placeholder={
            adminNote
              ? t('job.info.notes.textbox.placeholder.admin')
              : t('job.info.notes.textbox.placeholder.regular')
          }
        />
        <TextEditor.Mention suggestions={followers} getUser={(id) => userStore.items.get(id)} />
        <TextEditor.Actions>
          {(me.role === 'admin' || me.role === 'superadmin') && (
            <Box flex alignItems="center" justifyContent="flex-start" grow>
              <Switch
                key={adminNote.toString()}
                defaultValue={adminNote}
                onChange={() => setAdminNote(!adminNote)}
                label={t('job.info.notes.textbox.switch.admin')}
              />
              <Box ml="8" flex center>
                <InfoTooltip tooltip={t('job.info.notes.textbox.info')} />
              </Box>
            </Box>
          )}
          <Button
            variant="primary"
            onClick={saveNote}
            isDisabled={adding || isEmpty}
            text={
              adminNote
                ? t('job.info.notes.textbox.button.submit.admin')
                : t('job.info.notes.textbox.button.submit.regular')
            }
          />
        </TextEditor.Actions>
      </TextEditor>
      {adminNote && <AdminNoteInfo text={t('job.info.notes.textbox.info')} />}
    </Box>
  );
});

interface NoteEditProps {
  job: IJob;
  note: IJobNote;
  onCancel: () => void;
}

const NoteEdit = observer((props: NoteEditProps) => {
  const { note, onCancel, job } = props;
  const { userStore, t } = useStore();
  const [adding, setAdding] = useState(false);
  const { editorState, setEditorState, isEmpty } = useEditorState(note.text);
  useEffect(() => {
    note.edit();
  }, [note]);

  const suggestions = useMemo(() => {
    let followers = [...job.activeFollowers];
    if (note.isAdminNote) {
      followers = followers.filter((f) => f.role === 'admin');
    }
    return followers;
  }, [job.activeFollowers, note.isAdminNote]);

  async function updateNote() {
    setAdding(true);
    const text = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
    const success = await note.update(text);
    success && onCancel();
  }

  return (
    <TextEditor focusOnMount variant="orange" editorState={editorState} onChange={setEditorState}>
      <TextEditorToolbar />
      <TextEditor.Mention
        editMode
        suggestions={suggestions}
        getUser={(id) => userStore.items.get(id)}
      />
      <TextEditor.Input />
      <TextEditor.Actions>
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Button
            variant="primary-red"
            onClick={onCancel}
            text={t('job.info.notes.edit.button.cancel')}
          />
          <Button
            variant="primary"
            onClick={updateNote}
            isDisabled={isEmpty || adding}
            text={t('job.info.notes.edit.button.submit')}
          />
        </Stack>
      </TextEditor.Actions>
    </TextEditor>
  );
});
